import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { usePathParam } from '@chilecompra/react-kit'
import { Skeleton, Table, TableRow, Toast, Typography } from '@chilecompra/react-kit/components'
import ProtectByProfile from '../../components/Security/ProtectByProfile'
import TablePlaceholder from '../../components/Placeholders/TablePlaceholder'
import { USER_TYPE, WSP_COMPANY_TYPE_DICTIONARY } from '../../config/settings/constant'
import { PUBLIC_URL, REACT_APP_BASE_URL } from '../../config/settings/environment'
import useReCaptcha from '../../modules/hooks/reCaptcha'
import { useAuthorization } from '../AuthorizationProvider/AuthorizationProvider.hooks'
import { onGetSellerAffidavitGridThunk } from './PartnershipGridSection.actions'
import NotPublicPartnershipMessage from './PartnershipGridSection.NotPublicPartnershipMessage'
import PublicPartnershipMessage from './PartnershipGridSection.PublicPartnershipMessage'
import { Content, Link, Separator, TableCell } from './PartnershipGridSection.styles'

/**
 * The PartnershipGridSection's container.
 */
const PartnershipGridSection = () => {
  const { userType } = useAuthorization()
  const dispatch = useDispatch()
  const dni = usePathParam('rut')
  const { executeReCaptcha } = useReCaptcha()

  const { sellerType } = useSelector(state => state.basicSellerInformation)

  const { error, hasAffidavit, isForcedAffidavit, loading, otherPartners, partnerships, taxPayerType } = useSelector(
    state => state.partnershipGridSection
  )
  const { error: summaryError } = useSelector(state => state.summarySection)

  useEffect(() => {
    if (!loading || sellerType === '' || sellerType === 'natural') return

    if (userType === USER_TYPE.PUBLIC) {
      const executeAll = async () => {
        await executeReCaptcha({ action: 'publicInfo' })
        dispatch(onGetSellerAffidavitGridThunk({ dni, userType }))
      }

      executeAll()
    } else {
      dispatch(onGetSellerAffidavitGridThunk({ dni, userType }))
    }
  }, [sellerType])

  if (error?.reason === 'FORBIDDEN_ERROR' || sellerType === 'natural' || summaryError?.reason === 'FORBIDDEN_ERROR') {
    return null
  }

  if (WSP_COMPANY_TYPE_DICTIONARY[taxPayerType] && !partnerships?.length) return null

  return (
    <Content>
      <Typography fontWeight="bold" variant="h4">
        Socios y accionistas de la empresa proveedora
      </Typography>

      {loading && (
        <>
          <Skeleton margin="0 0 8px" width="100%" />
          <TablePlaceholder margin="24px 0 0" />
        </>
      )}

      {error && !loading && (
        <Toast
          border
          margin="24px 0 0"
          severity="error"
          textDirection="vertical"
          title="Lo sentimos, estamos presentando problemas con nuestro servidor"
        >
          En estos momentos tenemos problemas para disponibilizar la información. Por favor, consulta en unos minutos
          más.
        </Toast>
      )}

      {!error && !loading && (
        <>
          <Typography padding="0 0 8px" variant="subtitle">
            Fuente: Declaración Jurada del Registro de Proveedores ingresada por el proveedor en Mercado Público
          </Typography>

          {hasAffidavit && partnerships?.length > 0 && (
            <Typography color="black3" fontWeight="regular" margin="18px 0" variant="body2">
              Esta es la información relacionada a las sociedades y el porcentaje de participación de cada socio de la
              empresa proveedora. La información a continuación solamente se muestra hasta el primer nivel de
              profundidad, con todos los porcentajes de participación.
            </Typography>
          )}

          {userType === 'Publico' ? (
            <PublicPartnershipMessage
              hasAffidavit={hasAffidavit}
              isForcedAffidavit={isForcedAffidavit}
              partnershipCount={partnerships?.length || 0}
            />
          ) : (
            <NotPublicPartnershipMessage
              dni={dni}
              hasAffidavit={hasAffidavit}
              isForcedAffidavit={isForcedAffidavit}
              partnershipCount={partnerships?.length || 0}
            />
          )}

          {hasAffidavit && partnerships?.length > 0 && (
            <>
              <Table
                accessibility={{ label: 'Detalle Socios y Sociedades' }}
                disableLatestBottomBorder
                margin="24px 0 0"
              >
                <TableRow>
                  <TableCell>
                    <Typography fontWeight="bold" variant="body1">
                      Nombre
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Typography fontWeight="bold" variant="body1">
                      Rut
                    </Typography>
                  </TableCell>

                  <TableCell alignment="right" width="220px">
                    <Typography fontWeight="bold" variant="body1">
                      Porcentaje de participación
                    </Typography>
                  </TableCell>
                </TableRow>

                {partnerships?.map(partnership => (
                  <TableRow key={partnership.name}>
                    <TableCell>
                      <ProtectByProfile rule={userType => userType === 'Comprador'}>
                        {partnership.isNaturalPerson && !partnership.isForeign && partnership.name}

                        {(!partnership.isNaturalPerson || partnership.isForeign) && (
                          <Link href={`${REACT_APP_BASE_URL}${PUBLIC_URL}/${partnership.dni}`}>{partnership.name}</Link>
                        )}
                      </ProtectByProfile>

                      <ProtectByProfile rule={userType => userType !== 'Comprador'}>
                        {partnership.name}
                      </ProtectByProfile>
                    </TableCell>

                    <TableCell alignment="left" width="137px">
                      <ProtectByProfile rule={userType => userType !== 'Publico'}>{partnership.dni}</ProtectByProfile>

                      <ProtectByProfile rule={userType => userType === 'Publico'}>
                        {!partnership.dni || partnership.isNaturalPerson ? '-' : partnership.dni}
                      </ProtectByProfile>
                    </TableCell>

                    <TableCell alignment="right" width="137px">
                      {partnership.percentage}
                    </TableCell>
                  </TableRow>
                ))}

                {otherPartners !== undefined && (
                  <TableRow>
                    <TableCell>
                      {`OTROS SOCIOS (${otherPartners.numberOfPartners} SOCIO${
                        otherPartners.numberOfPartners > 1 ? 'S' : ''
                      })`}
                    </TableCell>
                    <TableCell />
                    <TableCell alignment="right" width="137px">
                      {otherPartners.percentage}
                    </TableCell>
                  </TableRow>
                )}
              </Table>

              <ProtectByProfile rule={userType => userType === 'Comprador'}>
                <Toast margin="24px 0 0" severity="info">
                  Puedes revisar la ficha de cada proveedor Persona Jurídica pinchando su razón social o en su RUT.
                </Toast>
              </ProtectByProfile>
            </>
          )}

          <Separator />
        </>
      )}
    </Content>
  )
}

export default PartnershipGridSection
